import { useState, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    MenuItem,
    Box,
    IconButton,
    styled,
    Backdrop,
    Menu,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StatusPill from '../../../Shared/Components/StatusPill';
import { COLORS } from '../../../utils/Application_Constants';
import { PROCESSED_TIMESHEET_STATUS_LABELS } from '../../constants';
import InvoiceReviewResolutionForm from './InvoiceReviewResolutionForm';
import UpdateStatusReasonForm from './UpdateStatusReasonForm';
import { useProcessedTimesheetContext } from '../../Contexts/ProcessedTimesheetContext';
import { getMonthPostedWindowWarning } from '../../Utils/validateMonthPostedWindow';
import useGetHolidays from '../../hooks/useGetHolidays';

const BackdropBlur = styled(Backdrop)({
    backdropFilter: 'blur(6px)',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
});

const UpdateStatusButtonModal = ({
    id,
    onClose,
    onSuccess,
    currentStatus,
    newStatus,
    setNewStatus,
    statusOptions = [],
    lastModified,
    previousCreditMemoMode,
    processedTimesheetEntries = [],
    locations = [],
    monthPostedDate,
    isCorrection = false,
}) => {
    const statusButton = useRef(null);
    const [openStatusMenu, setOpenStatusMenu] = useState(false);
    const [calculationErrors, setCalculationErrors] = useState([]);
    const { setCalculationErrorEntryIds, clearCalculationErrorEntryIds } =
        useProcessedTimesheetContext();
    const isResolvingInvoice = currentStatus === 'InvoiceReview' && newStatus === 'Resolved';
    const { data: holidays, isLoading: isLoadingHolidays } = useGetHolidays();
    // Wait for holidays to load so we don't briefly show the wrong message, then flip once it does.
    const monthPostedWarning = isLoadingHolidays
        ? null
        : getMonthPostedWindowWarning({
              monthPostedDate,
              newStatus,
              isCorrection,
              holidays,
          });

    const handleOpenMenu = () => {
        setOpenStatusMenu(true);
    };
    const handleCloseMenu = () => {
        setOpenStatusMenu(false);
    };
    const handleSelectNewStatus = (state) => {
        setNewStatus(state);
        handleCloseMenu();
        setCalculationErrors([]);
    };

    const handleClose = useCallback(() => {
        if (calculationErrors.length > 0) {
            const uniqueEntryIds = [...new Set(calculationErrors.map(({ entryId }) => entryId))];
            setCalculationErrorEntryIds(uniqueEntryIds);
        } else {
            clearCalculationErrorEntryIds();
        }

        setCalculationErrors([]);
        onClose();
    }, [calculationErrors, onClose, setCalculationErrorEntryIds, clearCalculationErrorEntryIds]);

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            slots={{ backdrop: BackdropBlur }}
            slotProps={{ paper: { sx: { borderRadius: '8px', padding: '48px 36px 24px' } } }}
        >
            <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 12, right: 12 }}>
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ padding: '0px' }}>
                <Box display="flex" justifyContent="flex-start" gap="50px" mb={2}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap="8px">
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                lineHeight: '100%',
                                letterSpacing: '0.07rem',
                                textTransform: 'uppercase',
                                color: COLORS.LT_MIDNIGHT500,
                            }}
                        >
                            FROM
                        </Typography>
                        <StatusPill status={currentStatus} sx={{ padding: 2 }} />
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap="8px">
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                lineHeight: '100%',
                                letterSpacing: '0.07rem',
                                textTransform: 'uppercase',
                                color: COLORS.LT_MIDNIGHT500,
                            }}
                        >
                            TO
                        </Typography>
                        <StatusPill
                            status={newStatus}
                            content={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    {PROCESSED_TIMESHEET_STATUS_LABELS[newStatus] ||
                                        'Select status'}
                                    <KeyboardArrowDownIcon
                                        sx={{
                                            ml: 'auto',
                                            transition: 'transform 0.3s',
                                            transform: openStatusMenu
                                                ? 'rotate(180deg)'
                                                : 'rotate(0deg)',
                                        }}
                                    />
                                </Box>
                            }
                            sx={{ padding: 2 }}
                            ref={statusButton}
                            onClick={handleOpenMenu}
                        />

                        <Menu
                            id="change-status-options"
                            open={openStatusMenu}
                            anchorEl={statusButton.current}
                            onClose={handleCloseMenu}
                        >
                            {statusOptions.map((state) => (
                                <MenuItem
                                    key={state}
                                    onClick={() => handleSelectNewStatus(state)}
                                    disableRipple
                                >
                                    <StatusPill status={state} sx={{ minWidth: 146 }} />
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Box>
                {monthPostedWarning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {monthPostedWarning}
                    </Alert>
                )}
                {isResolvingInvoice ? (
                    <InvoiceReviewResolutionForm
                        key={newStatus}
                        id={id}
                        onClose={handleClose}
                        onSuccess={onSuccess}
                        previousCreditMemoMode={previousCreditMemoMode}
                        processedTimesheetEntries={processedTimesheetEntries}
                        locations={locations}
                        onCalculationErrorsChange={setCalculationErrors}
                    />
                ) : (
                    <UpdateStatusReasonForm
                        key={newStatus}
                        onSuccess={onSuccess}
                        id={id}
                        lastModified={lastModified}
                        newStatus={newStatus}
                        onClose={handleClose}
                        processedTimesheetEntries={processedTimesheetEntries}
                        locations={locations}
                        onCalculationErrorsChange={setCalculationErrors}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UpdateStatusButtonModal;
